import { css, styled } from 'canoe';
import React, { MutableRefObject, useRef } from 'react';

import { Size } from 'craft/components/Size';
import { useOffsetFromViewport } from 'craft/hooks';
import { isDomElement, isSourceElement } from 'craft/utils/viewport';

import { DraggingLabel } from './DraggingLabel';
import { ResizeBoundaries } from './ResizeBoundaries';
import { ResizingAgent } from './ResizingAgent';
import { convertPixelsToSize } from './domManipulations';

export const RESIZE_BOUNDARIES_CONTAINER_ID = 'resize-boundaries-container';

const Wrapper = styled.div<{
  top: number;
  left: number;
  width: number;
  height: number;
}>`
  ${({ top, left, width, height }) => css`
    z-index: 2;
    position: absolute;
    top: ${top}px;
    left: ${left}px;
    width: ${width}px;
    height: ${height}px;
    pointer-events: none;
  `}
`;

const ResizeBoundariesContainer = styled.div<{
  hide?: boolean;
}>`
  ${({ hide }) => css`
    opacity: ${hide ? '0' : '1'};
  `}
`;

export const setResizeHandleVisibility = (visible: boolean) => {
  const resizeHandlesContainers = Array.from(
    document.querySelectorAll(`[id^="${RESIZE_BOUNDARIES_CONTAINER_ID}-visibility`),
  ) as HTMLElement[];
  if (resizeHandlesContainers.length) {
    resizeHandlesContainers
      .filter((dom) => isDomElement(dom))
      .forEach((resizeHandle) => {
        // eslint-disable-next-line no-param-reassign
        resizeHandle.style.opacity = visible ? '1' : '0';
      });
  }
};

interface Props {
  dom: HTMLElement;
  size: Size;
  showDraggingLabel: boolean;
  resizingAgent: MutableRefObject<ResizingAgent | undefined>;
  allowPercentages?: boolean;
}

export const ResizeBoundariesObserver = ({
  dom,
  resizingAgent,
  showDraggingLabel,
  size,
  allowPercentages,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const { top, left, scrollTop, scrollLeft, documentOffsetLeft } = useOffsetFromViewport(dom, ref);

  if (!isDomElement(dom) || !document.contains(dom)) {
    return null;
  }

  // you can't have less than 0 top or 0 left. this means that some element
  // somewhere is not present, or is only partly visible
  // so we should just not be rendering the boundaries
  if (top - scrollTop < 0 || left + documentOffsetLeft - scrollLeft < 0) {
    return null;
  }

  const width = dom.offsetWidth;
  const height = dom.offsetHeight;

  const parentWidth = dom.parentElement?.offsetWidth;

  const nodeId = dom.getAttribute('data-nid');
  const isMappedOverSource = isSourceElement(dom);

  return (
    <Wrapper
      id={`${RESIZE_BOUNDARIES_CONTAINER_ID}-${nodeId}`}
      key={`${nodeId}-${top + left + scrollTop + scrollLeft}`}
      data-craft-ignore-events
      top={top - scrollTop}
      left={left + documentOffsetLeft - scrollLeft}
      width={width}
      height={height}
    >
      <ResizeBoundariesContainer
        id={`${RESIZE_BOUNDARIES_CONTAINER_ID}-visibility-${nodeId}`}
        ref={ref}
        hide={!ref.current}
      >
        <ResizeBoundaries
          key={nodeId}
          onMouseDown={(e) => resizingAgent?.current?.onMouseDown(e)}
          width={width}
          height={height}
          isMappedOverSource={isMappedOverSource}
        />
      </ResizeBoundariesContainer>
      {showDraggingLabel && (
        <DraggingLabel
          width={width}
          height={height}
          isMappedOverSource={isMappedOverSource}
          axis={resizingAgent?.current?.draggingStart?.axis}
          latestSize={convertPixelsToSize(
            {
              width: resizingAgent?.current?.draggingStart?.latestWidth,
              height: resizingAgent?.current?.draggingStart?.latestHeight,
            },
            {
              width: resizingAgent?.current?.draggingStart?.startingWidth,
              height: resizingAgent?.current?.draggingStart?.startingHeight,
            },
            size,
            parentWidth,
            allowPercentages,
          )}
          originalSize={size}
        />
      )}
    </Wrapper>
  );
};
